<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Gerenciar Motoristas</h1>
        <p class="text-sm text-slate-500">Visualize e gerencie todos os motoristas cadastrados no sistema</p>
      </div>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- BARRA DE FERRAMENTAS DA TABELA -->
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">MOTORISTAS CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <!-- PREFEITURAS -->
                <div class="relative w-72" v-if="!$store.getters.getUserData.table_id">
                  <v-autocomplete
                    :items="prefectures"
                    v-model="filterPrefecture"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    placeholder="TODAS AS PREFEITURAS"
                    dark dense hide-details
                    class="pt-0 mt-0 text-[11px] font-bold tracking-widest uppercase"
                    color="orange"
                    clearable
                  ></v-autocomplete>
                </div>

                <!-- PESQUISA EXPANSSÍVEL -->
                <div class="relative transition-all duration-300" :class="showSearch || search ? 'w-80' : 'w-10'">
                  <button 
                    v-if="!showSearch && !search" 
                    @click="showSearch = true; $nextTick(() => $refs.searchInput.focus())" 
                    class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                    title="Pesquisar..."
                  >
                    <v-icon color="white" small>mdi-magnify</v-icon>
                  </button>
                  <div v-else class="relative w-full">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                    </div>
                    <input 
                      ref="searchInput"
                      v-model="search"
                      @blur="if(!search) showSearch = false"
                      placeholder="PESQUISAR MOTORISTA..."
                      class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
                </div>

                <!-- BOTAO NOVO -->
                <button 
                  @click="$router.push('/drivers-manager/create')" 
                  class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-max whitespace-nowrap"
                  style="width: auto !important; flex: none !important;"
                >
                  <v-icon color="white" small>mdi-plus</v-icon>
                  <span class="font-bold text-sm">NOVO</span>
                </button>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="filteredData"
                :search="search"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :loading="loader"
                loading-text="CARREGANDO..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.driver_category="{ item }">
                  <span class="font-bold text-slate-700">{{ item.driver_category || '--' }}</span>
                </template>

                <template v-slot:item.driver_date_validity="{ item }">
                  <span class="text-slate-600 font-medium whitespace-nowrap">{{ item.driver_date_validity || '--' }}</span>
                </template>

                <template v-slot:item.cnh_status="{ item }">
                  <span :class="getCnhStatusClass(item.driver_date_validity)" class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                    {{ getCnhStatusText(item.driver_date_validity) }}
                  </span>
                </template>

                <template v-slot:item.driver_active="{ item }">
                   <span :class="item.driver_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'" class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                        {{ item.driver_active ? "ATIVO" : "INATIVO" }}
                    </span>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2 py-2">
                    <button 
                      @click="toggleActive(item)"
                      class="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      :title="item.driver_active ? 'Bloquear Acesso' : 'Liberar Acesso'"
                    >
                      <v-icon small :color="item.driver_active ? 'success' : 'grey'">{{ item.driver_active ? 'mdi-account-check' : 'mdi-account-remove' }}</v-icon>
                    </button>
                    <button 
                      @click="showCredentials(item)"
                      class="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                      title="Credenciais"
                    >
                      <v-icon small color="blue">mdi-key-variant</v-icon>
                    </button>
                    <button 
                      @click="$router.push(`/drivers-manager/edit/${item.driver_id}`)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.driver_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                    <p class="mt-2 text-sm">Nenhum motorista encontrado</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Custom Pagination Footer -->
              <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-xs text-slate-500 font-medium w-full">
                  <span>Exibindo {{ paginationStart }} de {{ filteredData.length }} registros</span>
                  
                  <div class="flex gap-1">
                      <button 
                        @click="page > 1 ? page-- : null" 
                        :disabled="page === 1"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Anterior
                      </button>
                      
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                        class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                      >
                        {{ p }}
                      </button>

                      <button 
                        @click="page < pageCount ? page++ : null" 
                        :disabled="page === pageCount"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Próximo
                      </button>
                  </div>
              </div>
          </div>
      </section>
    </div>

    <!-- DIALOG CREDENCIAIS -->
    <v-dialog v-model="credentialsDialog" max-width="450px" centered transition="dialog-bottom-transition">
      <v-card class="rounded-2xl overflow-hidden">
        <div class="bg-slate-800 px-6 py-4 flex items-center justify-between">
          <h3 class="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2">
            <v-icon color="orange" small>mdi-lock-check-outline</v-icon>
            Credenciais de Acesso
          </h3>
          <button @click="credentialsDialog = false" class="text-slate-400 hover:text-white transition-colors">
            <v-icon color="inherit">mdi-close</v-icon>
          </button>
        </div>
        
        <v-card-text class="p-8">
          <div class="space-y-6">
            <div class="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <label class="block text-[10px] font-bold uppercase text-slate-400 mb-2">Login de Acesso</label>
              <div class="flex items-center justify-between">
                <span class="text-slate-700 font-mono font-bold">{{ selectedDriver.driver_login || 'Não informado' }}</span>
                <button 
                  @click="copyToClipboard(selectedDriver.driver_login, 'Login')"
                  class="text-orange-500 hover:text-orange-600 p-1 rounded transition-colors"
                >
                  <v-icon x-small color="inherit">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>

            <div class="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <label class="block text-[10px] font-bold uppercase text-slate-400 mb-2">Senha Provisória</label>
              <div class="flex items-center justify-between">
                <span class="text-slate-700 font-mono font-bold">{{ selectedDriver.driver_password || 'Não informada' }}</span>
                <button 
                  @click="copyToClipboard(selectedDriver.driver_password, 'Senha')"
                  class="text-orange-500 hover:text-orange-600 p-1 rounded transition-colors"
                >
                  <v-icon x-small color="inherit">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>
          </div>
        </v-card-text>

        <div class="px-8 pb-8 flex justify-center">
          <button 
            @click="credentialsDialog = false"
            class="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-[10px] uppercase tracking-widest py-3 rounded-lg transition-all"
          >
            Fechar Janela
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition" rounded="pill">
      <div class="flex items-center gap-3">
         <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
         <span class="font-bold text-xs uppercase tracking-wide">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    </div>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      showSearch: false,
      filterPrefecture: null,
      prefectures: [],
      loader: true,
      credentialsDialog: false,
      selectedDriver: {},
      data: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "NOME DO MOTORISTA", align: "start", value: "driver_name" },
        { text: "CÓDIGO", value: "driver_cod" },
        { text: "CPF", value: "driver_cpf" },
        { text: "CONTATO", value: "driver_cel" },
        { text: "CAT. CNH", value: "driver_category", align: "center" },
        { text: "VALIDADE CNH", value: "driver_date_validity", align: "center" },
        { text: "STATUS CNH", value: "cnh_status", align: "center" },
        { text: "STATUS", value: "driver_active", align: "center" },
        { text: "AÇÕES", align: "right", value: "action", sortable: false }
      ]
    };
  },
  computed: {
    filteredData() {
      if (!this.filterPrefecture) return this.data;
      return this.data.filter(d => d.prefecture_id === this.filterPrefecture);
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    getCnhStatusText(dateStr) {
      if (!dateStr || dateStr.length !== 10) return "NÃO INFORMADA";
      const parts = dateStr.split("/");
      if (parts.length !== 3) return "INVÁLIDA";
      
      const validityDate = new Date(parts[2], parts[1] - 1, parts[0]);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      const diffTime = validityDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return "VENCIDA";
      if (diffDays <= 30) return "VENCE EM BREVE";
      return "REGULAR";
    },
    getCnhStatusClass(dateStr) {
      const status = this.getCnhStatusText(dateStr);
      if (status === "VENCIDA") return "bg-red-100 text-red-700";
      if (status === "VENCE EM BREVE") return "bg-orange-100 text-orange-700";
      if (status === "REGULAR") return "bg-emerald-100 text-emerald-700";
      return "bg-slate-100 text-slate-500";
    },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      
      let url = "/drivers";
      if (this.$store.getters.getUserData.table_id) {
        url = `/drivers/prefectures/${this.$store.getters.getUserData.table_id}`;
      } else {
        this.$http.get("/prefectures")
          .then(r => this.prefectures = r.data)
          .catch(e => console.error(e));
      }

      this.$http
        .get(url)
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e.message || "Erro ao carregar dados";
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    showCredentials(item) {
      this.selectedDriver = Object.assign({}, item);
      this.credentialsDialog = true;
    },
    copyToClipboard(text, field) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.snack.show = true;
        this.snack.message = `${field} copiado!`;
        this.snack.color = "info";
      });
    },
    deleteItem(id) {
      if (confirm("Deseja realmente excluir este motorista? Esta ação não pode ser desfeita.")) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/drivers/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result || "Motorista removido!";
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    toggleActive(item) {
      const newStatus = !item.driver_active;
      const msg = newStatus ? "Deseja liberar o acesso deste motorista?" : "Deseja bloquear o acesso deste motorista?";
      
      if (confirm(msg)) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/drivers/${item.driver_id}`, { driver_active: newStatus })
          .then(() => {
            this.snack.show = true;
            this.snack.message = newStatus ? "Acesso liberado com sucesso!" : "Acesso bloqueado com sucesso!";
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-700 { background-color: #333333; }
.border-slate-200 { border-color: #e2e8f0; }
.text-slate-500 { color: #64748b; }
.text-slate-700 { color: #334155; }

.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
  padding: 12px 24px !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #fcfcfc !important;
}
</style>